import {
  CouponInfoSheet,
  Coupon as CouponType,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {excludeQueryParams, useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import React, {FC, memo} from 'react';
import {useIsUrgencyTriggersDisabled} from '../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {getCouponPromotionId} from '../Coupons/getCouponPromotionId';
import {useActivatedCouponState} from '../Coupons/useActivatedCouponState';
import {
  COUPON_PROMOTION_ID_QUERY_KEY,
  useCouponPromotionIdFromQueryParams,
} from '../Coupons/useCouponPromotionIdFromQueryParams';
import {CouponCardBase} from './CouponCardBase';

const CouponCard: FC<{
  ['data-testid']?: string;
  className?: string;
  productId?: number;
  coupon: CouponType;
  onInfoIconClick?: (couponInfo?: CouponInfoSheet) => void;
  stateManagerCustomKey?: string;
  /**
   * When `isDisplayMode` is true, the coupon card cannot be clicked to activate the coupon.
   * It also will have a view only style specific to the wallet / coupon unlock success popups.
   */
  isDisplayMode: boolean;
  isDisabled?: boolean;
  setSelectedProduct?: (product: Product | undefined) => void;
  isInWallet?: boolean;
}> = ({
  'data-testid': dataTestId = 'couponCard',
  className,
  coupon,
  productId,
  onInfoIconClick,
  stateManagerCustomKey,
  isDisplayMode,
  isDisabled,
  setSelectedProduct,
  isInWallet,
}) => {
  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();
  const {couponPromotionId: activatedCouponPromotionId, setActivatedCoupon} =
    useActivatedCouponState(productId);
  const trackFeature = useTrackFeature();
  const couponPromotionId = getCouponPromotionId(coupon);
  const couponPromotionIdFromQueryParams =
    useCouponPromotionIdFromQueryParams();

  const history = useHistory();

  const activateCouponCallback = () => {
    if (isDisplayMode) {
      return;
    }

    if (couponPromotionIdFromQueryParams) {
      const queryParams = new URLSearchParams(history.location.search);
      // remove the couponId query parameter from the URL
      history.replace({
        pathname: history.location.pathname,
        search: excludeQueryParams(
          queryParams,
          new Set([COUPON_PROMOTION_ID_QUERY_KEY]),
        ).toString(),
        state: history.location.state,
      });
    }

    /**
     * When de-selecting a coupon, we set the activatedCoupon to null explicitly,
     * so that we can distinguish between two states:
     * 1. Coupon is not selected  initially (activatedCoupon is undefined)
     * 2. Coupon is de-selected manually (activatedCoupon is null)
     */
    const couponToActivate =
      activatedCouponPromotionId === couponPromotionId ? null : coupon;

    setActivatedCoupon(couponToActivate);

    if (couponToActivate) {
      trackFeature({
        eventType: 'submit',
        label: couponPromotionId,
        name: 'coupon_banner',
      });
    }
  };

  const activateCouponOnAddToBasket = () => {
    setActivatedCoupon(coupon);
  };

  const defaultProps = {
    className,
    'data-testid': dataTestId,
    activateCouponCallback,
    onInfoIconClick,
    stateManagerCustomKey,
    isDisplayMode,
    isDisabled,
    setSelectedProduct,
    activateCouponOnAddToBasket,
    isInWallet,
    disableUrgencyTriggers,
  };

  switch (coupon.type?.$case) {
    case 'simpleCoupon': {
      const simpleCoupon = coupon.type.simpleCoupon;

      return (
        <CouponCardBase
          {...simpleCoupon}
          {...defaultProps}
          isActive={
            simpleCoupon.couponPromotionId === activatedCouponPromotionId
          }
        />
      );
    }

    case 'hotDealCoupon': {
      const hotDealCoupon = coupon.type.hotDealCoupon;

      return (
        <CouponCardBase
          {...hotDealCoupon}
          {...defaultProps}
          isActive={
            hotDealCoupon.couponPromotionId === activatedCouponPromotionId &&
            !isDisplayMode
          }
          isHotDeal
        />
      );
    }

    case 'minimumOrderValueCoupon': {
      const minimumOrderValueCoupon = coupon.type.minimumOrderValueCoupon;

      return (
        <CouponCardBase
          {...minimumOrderValueCoupon}
          {...defaultProps}
          isActive={
            minimumOrderValueCoupon.couponPromotionId ===
            activatedCouponPromotionId
          }
        />
      );
    }

    case 'highTierCoupon': {
      const highTierCoupon = coupon.type.highTierCoupon;

      const discountValueOrTiers =
        highTierCoupon.discount?.$case === 'relativeDiscount'
          ? {
              discountValue: highTierCoupon.discount.relativeDiscount,
            }
          : {
              tiers: highTierCoupon.discount?.tiers.tiers,
            };

      return (
        <CouponCardBase
          {...discountValueOrTiers}
          {...highTierCoupon}
          {...defaultProps}
          isActive={
            highTierCoupon.couponPromotionId === activatedCouponPromotionId
          }
        />
      );
    }

    default:
      return null;
  }
};

const MemoizedCouponCard = memo(CouponCard);

export {MemoizedCouponCard as CouponCard};
